import { useEffect, useState } from 'react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { LayoutGrid, ExternalLink } from 'lucide-react'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { COMPANY_SPECIFIC_ROLES, PROPERTY_ROLES } from '@/constants/roles'
import { checkGdprConsent, GdprConsentModal } from '@/features/document-management'
import {
  GDPR_CONSENT_PENDING_KEY,
  GDPR_CONSENT_REQUIRED_EVENT,
} from '@/features/document-management/constants/gdprConsent'
import { useSystemAnnouncementPopup } from '@/features/system-announcement'
import '@/features/system-announcement/styles/announcementHtml.css'
import { EORPModal } from '../components/EORPModal'
import { useDashboardTiles } from '../hooks/useDashboardTiles'
import { useDashboardSystemAnnouncement } from '../hooks/useDashboardSystemAnnouncement'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { isImpersonating } from '@/lib/utils/storage'
import { isEorpTile, openDashboardTile, tileOpensInNewTab } from '../utils/dashboardTileNavigation'
import styles from '../styles/dashboardTiles.module.css'

export function Dashboard() {
  const user = useAppSelector(selectUser)
  const roleName = user?.current_role?.role_name ?? ''
  const [showEorpModal, setShowEorpModal] = useState(false)
  const [showGdprConsentModal, setShowGdprConsentModal] = useState(() => {
    try {
      return sessionStorage.getItem(GDPR_CONSENT_PENDING_KEY) === '1'
    } catch {
      return false
    }
  })
  const { tiles, loading: tilesLoading, error: tilesError, reload: reloadTiles } =
    useDashboardTiles()
  const {
    html: systemAnnouncementHtml,
    loading: announcementLoading,
  } = useDashboardSystemAnnouncement()
  const { modal: systemAnnouncementModal } = useSystemAnnouncementPopup()

  useEffect(() => {
    // Angular parity: impersonated admins never hit the impersonated user's GDPR consent gate.
    if (isImpersonating()) return

    const shouldCheckConsent =
      (COMPANY_SPECIFIC_ROLES as readonly string[]).includes(roleName) ||
      (PROPERTY_ROLES as readonly string[]).includes(roleName)

    if (!shouldCheckConsent) return

    let cancelled = false
    void checkGdprConsent()
      .then((response) => {
        // Stay on dashboard so the dimmed page shows through (same as Send Email popup).
        if (!cancelled && response.data?.content) {
          try {
            sessionStorage.setItem(GDPR_CONSENT_PENDING_KEY, '1')
          } catch {
            /* ignore */
          }
          setShowGdprConsentModal(true)
        }
      })
      .catch(() => {
        // The global interceptor handles 307 redirects; already-signed/no-op
        // responses should not interrupt the dashboard.
      })

    return () => {
      cancelled = true
    }
  }, [roleName])

  const handleGdprAccepted = () => {
    try {
      sessionStorage.removeItem(GDPR_CONSENT_PENDING_KEY)
      localStorage.setItem('gdpr_consent_given', '1')
    } catch {
      /* ignore */
    }
    setShowGdprConsentModal(false)
    // Tiles (and other APIs) may have failed with 307 before consent — refetch now.
    reloadTiles()
  }

  useEffect(() => {
    const openFromInterceptor = () => setShowGdprConsentModal(true)
    window.addEventListener(GDPR_CONSENT_REQUIRED_EVENT, openFromInterceptor)
    return () => window.removeEventListener(GDPR_CONSENT_REQUIRED_EVENT, openFromInterceptor)
  }, [])

  // Angular parity: the EORP ("Explanation of the Reimbursement Process") popup opens
  // ONLY when the user clicks the reimbursement-agreement tile (see the tile onClick
  // below). Angular never auto-opens it on dashboard load, so React must not either.
  const handleCloseEorp = () => {
    setShowEorpModal(false)
  }

  const welcomeName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900">
      <PageHeader
        title="DASHBOARD"
        showBreadcrumbs={false}
        testIdRoot="dashboard-page-header"
      />
      <div className="p-3 sm:p-6">
        <EORPModal isOpen={showEorpModal} onClose={handleCloseEorp} />
        <GdprConsentModal
          isOpen={showGdprConsentModal}
          onAccepted={handleGdprAccepted}
        />
        {systemAnnouncementModal}

        {/*
          Welcome shell keeps current React UI; body HTML matches Angular
          `GET /api/system_announcement` (org System Announcements CMS).
        */}
        <div className="mb-4 sm:mb-8">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm">
            <span
              className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-teal-400 via-blue-500 to-violet-500"
              aria-hidden
            />
            <div className="p-5 pl-6 sm:p-7 sm:pl-8">
              <div className="min-w-0 max-w-full">
                <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-3xl break-words">
                  {welcomeName ? `Welcome, ${welcomeName}` : 'Welcome to Liberty Rent'}
                </h1>
              </div>

              {announcementLoading ? (
                <div
                  className="mt-3 h-16 animate-pulse rounded-lg bg-slate-100 sm:mt-4"
                  aria-hidden
                  data-testid="dashboard-system-announcement-loading"
                />
              ) : systemAnnouncementHtml ? (
                <div
                  className="announcement-html dashboard-welcome-announcement mt-2 min-w-0 max-w-full sm:mt-3"
                  data-testid="dashboard-system-announcement"
                  // Org-managed CMS HTML (same source as Angular dashboard banner)
                  dangerouslySetInnerHTML={{ __html: systemAnnouncementHtml }}
                />
              ) : null}
            </div>
          </div>
        </div>

        {/* Dashboard Tiles — config-driven, mirrors Angular dashboard-tile-report */}
        {tilesLoading ? (
          <ModuleScreenLoading
            variant="inline"
            className="rounded-2xl border border-slate-200/70 bg-white py-16"
            data-testid="dashboard-tiles-loading"
          />
        ) : tilesError ? (
          <div
            className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
            data-testid="dashboard-tiles-error"
          >
            {tilesError}
          </div>
        ) : tiles.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center"
            data-testid="dashboard-tiles-empty"
          >
            <LayoutGrid className="mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm font-medium text-slate-600">No tiles assigned</p>
            <p className="mt-1 text-xs text-slate-400">
              Your current role has no dashboard tiles configured.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 sm:gap-6">
            {tiles.map((tile) => {
              const eorp = isEorpTile(tile)
              const newTab = tileOpensInNewTab(tile)
              return (
                <button
                  key={tile.tile_id}
                  type="button"
                  data-testid={`dashboard-tile-${tile.tile_id}`}
                  onClick={() =>
                    eorp
                      ? setShowEorpModal(true)
                      : openDashboardTile(tile, { roleName })
                  }
                  className="group relative flex min-w-0 flex-col items-start rounded-2xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/70 sm:p-6"
                >
                  <span
                    className={`mb-3 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white shadow-md ring-4 ring-white transition-transform duration-200 group-hover:scale-110 sm:mb-4 ${styles.tileIcon}`}
                    style={
                      tile.font_color
                        ? { backgroundColor: tile.font_color }
                        : undefined
                    }
                  >
                    {tile.tile_icon ? (
                      <i
                        className={`${tile.tile_icon} text-xl leading-none`}
                        style={{ color: tile.icon_color || '#ffffff' }}
                        aria-hidden
                      />
                    ) : (
                      <LayoutGrid className="h-6 w-6" />
                    )}
                  </span>

                  <div className="flex w-full min-w-0 items-start justify-between gap-2">
                    <h3 className="mb-1 min-w-0 flex-1 break-words text-base font-bold text-slate-900 sm:mb-2 sm:text-lg">
                      {tile.tile_name}
                    </h3>
                    {newTab && (
                      <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-slate-300" aria-hidden />
                    )}
                  </div>
                  <p className="w-full min-w-0 break-words text-xs leading-snug text-slate-500 sm:text-sm">
                    {tile.tile_description}
                  </p>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
